import { incApi } from 'api/inc-api';
import { getClientId } from 'util/client-id';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import {
  AccountChangeLogType,
  Customer,
  CustomerTag,
  CustomerTagMutation,
  GetCustomerTagsResult,
} from './user-domain.types';
import { newCustomerSearchParams } from './user-domain.utils';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listCustomerTags: build.query<
      GetCustomerTagsResult,
      { count: number; page: number }
    >({
      query: params => ({
        url: 'incentivio-user-domain/customer-tags',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.tagAndRuleDTOS,
        element => element.id,
        'CustomerTag',
      ),
    }),

    getCustomerTag: build.query<CustomerTag, string>({
      query: id => ({
        url: `incentivio-user-domain/customer-tags/${id}`,
      }),
      providesTags: generateProvides(element => element.id, 'CustomerTag'),
    }),

    createCustomerTag: build.mutation<CustomerTag, CustomerTagMutation>({
      query: body => {
        return {
          url: `incentivio-user-domain/customer-tags`,
          method: 'POST',
          body: {
            key: body.title.replace(' ', '_').toUpperCase(),
            ...body,
          },
        };
      },
      invalidatesTags: ['CustomerTags'],
    }),

    updateCustomerTag: build.mutation<unknown, CustomerTagMutation>({
      query: body => {
        return {
          url: `incentivio-user-domain/customer-tags/${body.id}`,
          method: 'PUT',
          body: [
            'attributeType',
            'collectionValues',
            'description',
            'key',
            'source',
            'title',
          ].reduce((acc, key) => {
            acc[key] = body[key];
            return acc;
          }, {}),
        };
      },
      invalidatesTags: generateInvalidates(body => body.id, 'CustomerTag'),
    }),

    deleteCustomerTag: build.mutation<unknown, { id: string }>({
      query: ({ id }) => {
        return {
          url: `incentivio-user-domain/customer-tags/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['CustomerTags'],
    }),

    getListUsers: build.query({
      query: params => {
        return {
          url: `incentivio-user-domain/clients/${getClientId()}/users/filter`,
          params: newCustomerSearchParams(params),
        };
      },
      providesTags: generateProvidesList(
        result => result?.users,
        element => element.userId,
        'Customer',
      ),
    }),

    getCustomer: build.query<Customer, any>({
      query: id => ({
        url: `/incentivio-user-domain/clients/${getClientId()}/useraccounts/${id}`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.userId, 'Customer'),
      transformResponse: (response: Customer) => ({
        ...response,
        dateOfBirth: response.dateOfBirth?.replace(/^\d{4}-/, '') ?? '',
      }),
    }),

    updateCustomer: build.mutation<any, { userId: string; body: Customer }>({
      query: ({ userId, body }) => {
        return {
          url: `incentivio-user-domain/clients/${getClientId()}/useraccounts/${userId}/by-admin`,
          method: 'PUT',
          body: {
            dateOfBirth: body.dateOfBirth,
            email: body.email,
            firstName: body.firstName,
            gender: body.gender,
            lastName: body.lastName,
            phoneNumber: body.phoneNumber,
          },
        };
      },
      invalidatesTags: generateInvalidates(body => body.userId, 'Customer'),
    }),

    verifyEmail: build.mutation<any, { userId: string }>({
      query: ({ userId }) => {
        return {
          url: `incentivio-user-domain/clients/${getClientId()}/useraccounts/${userId}/verify`,
          method: 'POST',
        };
      },
      invalidatesTags: generateInvalidates(body => body.userId, 'Customer'),
    }),

    addRemoveCustomerTag: build.mutation<any, { userId: string; body }>({
      query: ({ userId, body }) => {
        const requestBody = [];
        if (body.remove) {
          requestBody.push({
            attribute: 'extendedAttributes',
            op: 'REMOVE',
            value: body.remove,
          });
        }
        if (body.add) {
          requestBody.push({
            attribute: 'extendedAttributes',
            op: 'ADD',
            value: body.add,
          });
        }
        return {
          url: `incentivio-user-domain/clients/${getClientId()}/useraccounts/${userId}`,
          method: 'PATCH',
          body: requestBody,
        };
      },
      invalidatesTags: generateInvalidates(body => body.userId, 'Customer'),
    }),

    getIsClientConfigReachEnabled: build.query<Boolean, void>({
      query: () => ({
        url: `incentivio-user-domain/clients/${getClientId()}/clientconfig/reach`,
        method: 'GET',
      }),
      transformResponse: response => Boolean(response),
    }),

    deleteCustomer: build.mutation<null, { userId: string; body }>({
      query: ({ userId, body }) => {
        return {
          url: `incentivio-user-domain/clients/${getClientId()}/useraccounts/${userId}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['Customers'],
    }),

    getResetPasswordLink: build.mutation<string, { emailUser: string }>({
      query: ({ emailUser }) => ({
        headers: {
          Accept: 'text/plain',
        },
        url: `incentivio-user-domain/users/${emailUser}/requestpwreseturl`,
        method: 'POST',
        responseHandler: 'text',
      }),
    }),

    blockUser: build.mutation<string, { reason: string; userId: string }>({
      query: ({ reason, userId }) => ({
        url: `incentivio-user-domain/useraccounts/${userId}/block`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: generateInvalidates(
        body => body.userId,
        ['Customer', 'BlockUnblockHistory'],
      ),
    }),

    unblockUser: build.mutation<string, { reason: string; userId: string }>({
      query: ({ reason, userId }) => ({
        url: `incentivio-user-domain/useraccounts/${userId}/unblock`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: generateInvalidates(
        body => body.userId,
        ['Customer', 'BlockUnblockHistory'],
      ),
    }),

    getAccountChangeLogs: build.query<
      AccountChangeLogType[],
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `incentivio-user-domain/useraccounts/${userId}/account-change-logs`,
        method: 'GET',
      }),
      providesTags: generateProvides(
        (_result, _error, arg) => arg.userId,
        'BlockUnblockHistory',
      ),
      transformResponse: (response: AccountChangeLogType[]) =>
        response.map((log, index) => ({
          ...log,
          id: index,
        })),
    }),
  }),
});

export const {
  useGetCustomerTagQuery,
  useUpdateCustomerTagMutation,
  useCreateCustomerTagMutation,
  useListCustomerTagsQuery,
  useDeleteCustomerTagMutation,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
  useVerifyEmailMutation,
  useAddRemoveCustomerTagMutation,
  useGetListUsersQuery,
  useGetIsClientConfigReachEnabledQuery,
  useDeleteCustomerMutation,
  useGetResetPasswordLinkMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useGetAccountChangeLogsQuery,
} = endpoints;
